import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Search, Filter, X, Calendar, MapPin, DollarSign, 
  Users, Gamepad2, SlidersHorizontal, ChevronDown, ChevronUp
} from "lucide-react";

const EventFilters = ({ filters, setFilters, onApply, onClear }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const gameTypes = ["Spades", "Whist", "Backgammon", "Checkers", "Chess", "Dominoes"];
  const skillLevels = ["beginner", "intermediate", "advanced", "expert", "all"];
  const sortOptions = [
    { value: "date_asc", label: "Date (Earliest)" },
    { value: "date_desc", label: "Date (Latest)" },
    { value: "participants", label: "Most Popular" },
    { value: "fee_asc", label: "Price (Low to High)" },
    { value: "fee_desc", label: "Price (High to Low)" },
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleGameType = (game) => {
    const current = filters.game_types || [];
    const updated = current.includes(game)
      ? current.filter(g => g !== game)
      : [...current, game];
    handleFilterChange("game_types", updated);
  };

  const activeFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.game_types?.length > 0) count++;
    if (filters.skill_level && filters.skill_level !== "all") count++;
    if (filters.date_from || filters.date_to) count++;
    if (filters.city) count++;
    if (filters.min_fee !== undefined || filters.max_fee !== undefined) count++;
    if (filters.has_spots) count++;
    return count;
  };

  return (
    <Card className="bg-[#0A0A0A] border-white/10 mb-6">
      <CardContent className="p-4">
        {/* Search Bar */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
            <Input
              value={filters.search || ""}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Search events by name, description..."
              className="input-dark pl-10"
              data-testid="event-search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setIsExpanded(!isExpanded)}
            className={`border-white/20 ${isExpanded ? "bg-white/10 text-white" : "text-[#A1A1AA]"}`}
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {activeFilterCount() > 0 && (
              <span className="ml-2 w-5 h-5 rounded-full bg-[#7a6200] text-black text-xs flex items-center justify-center">
                {activeFilterCount()}
              </span>
            )}
            {isExpanded ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </Button>
          <Button onClick={onApply} className="btn-gold" data-testid="apply-filters-btn">
            <Filter className="w-4 h-4 mr-2" />
            Apply
          </Button>
        </div>

        {/* Expanded Filters */}
        {isExpanded && (
          <div className="space-y-4 pt-4 border-t border-white/10">
            {/* Game Types */}
            <div>
              <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                <Gamepad2 className="w-4 h-4 inline mr-2" />
                Game Types
              </label>
              <div className="flex flex-wrap gap-2">
                {gameTypes.map((game) => (
                  <button
                    key={game}
                    onClick={() => toggleGameType(game)}
                    className={`px-3 py-1.5 rounded-full text-sm font-ubuntu transition-colors ${
                      (filters.game_types || []).includes(game)
                        ? "bg-[#7a6200] text-black"
                        : "bg-white/5 text-[#A1A1AA] border border-white/10 hover:border-[#7a6200]/50"
                    }`}
                    data-testid={`filter-game-${game.toLowerCase()}`}
                  >
                    {game}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range & Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  From Date
                </label>
                <Input
                  type="date"
                  value={filters.date_from || ""}
                  onChange={(e) => handleFilterChange("date_from", e.target.value)}
                  className="input-dark"
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  To Date
                </label>
                <Input
                  type="date"
                  value={filters.date_to || ""}
                  onChange={(e) => handleFilterChange("date_to", e.target.value)}
                  className="input-dark"
                  data-testid="filter-date-to"
                />
              </div>
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  City
                </label>
                <Input
                  value={filters.city || ""}
                  onChange={(e) => handleFilterChange("city", e.target.value)}
                  placeholder="Any location"
                  className="input-dark"
                  data-testid="filter-city"
                />
              </div>
            </div>

            {/* Skill Level & Entry Fee */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  <Users className="w-4 h-4 inline mr-2" />
                  Skill Level
                </label>
                <select
                  value={filters.skill_level || "all"}
                  onChange={(e) => handleFilterChange("skill_level", e.target.value)}
                  className="input-dark w-full h-10"
                  data-testid="filter-skill-level"
                >
                  {skillLevels.map((level) => (
                    <option key={level} value={level}>
                      {level === "all" ? "All Levels" : level.charAt(0).toUpperCase() + level.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  <DollarSign className="w-4 h-4 inline mr-2" />
                  Min Entry Fee
                </label>
                <Input
                  type="number"
                  min="0"
                  value={filters.min_fee ?? ""}
                  onChange={(e) => handleFilterChange("min_fee", e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="$0"
                  className="input-dark"
                  data-testid="filter-min-fee"
                />
              </div>
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  <DollarSign className="w-4 h-4 inline mr-2" />
                  Max Entry Fee
                </label>
                <Input
                  type="number"
                  min="0"
                  value={filters.max_fee ?? ""}
                  onChange={(e) => handleFilterChange("max_fee", e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="No limit"
                  className="input-dark"
                  data-testid="filter-max-fee"
                />
              </div>
            </div>

            {/* Additional Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  Sort By
                </label>
                <select
                  value={filters.sort_by || "date_asc"}
                  onChange={(e) => handleFilterChange("sort_by", e.target.value)}
                  className="input-dark w-full h-10"
                  data-testid="filter-sort-by"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.has_spots || false}
                    onChange={(e) => handleFilterChange("has_spots", e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#7a6200] focus:ring-[#7a6200]"
                    data-testid="filter-has-spots"
                  />
                  <span className="text-[#A1A1AA] font-ubuntu text-sm">Only show events with available spots</span>
                </label>
              </div>
            </div>

            {/* Clear Filters */}
            <div className="flex justify-end pt-2">
              <Button
                variant="ghost"
                onClick={onClear}
                className="text-[#A1A1AA] hover:text-white"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Clear All Filters
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EventFilters;
